import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitTsrFoodOrder, validateTsrFoodCoupon } from '../api/tsrFoodApi';
import { calcLineTotal, calcTotalQuantity, formatAmount, formatQtyWithUnit } from '../utils/orderFoodUtils';

const emptyForm = {
  customerName: '',
  phone: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: ''
};

function PlaceOrderStep({
  packageState,
  persons,
  items,
  packageTotal,
  verifiedChannel,
  verifiedIdentifier,
  profile,
  initialCouponCode,
  onBack,
  onClose
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [couponCode, setCouponCode] = useState(initialCouponCode || '');
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [orderTotal, setOrderTotal] = useState(packageTotal);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponValid, setCouponValid] = useState(false);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const clearCouponState = () => {
    setAppliedCoupon('');
    setCouponDiscount(0);
    setOrderTotal(packageTotal);
    setCouponMessage('');
    setCouponValid(false);
  };

  useEffect(() => {
    clearCouponState();
  }, [packageTotal, persons, items]);

  useEffect(() => {
    setForm({
      customerName: profile?.customerName || '',
      phone: profile?.phone || (verifiedChannel === 'phone' ? verifiedIdentifier : ''),
      email: profile?.email || (verifiedChannel === 'email' ? verifiedIdentifier : ''),
      addressLine1: profile?.addressLine1 || '',
      addressLine2: profile?.addressLine2 || '',
      city: profile?.city || '',
      state: profile?.state || '',
      pincode: profile?.pincode || ''
    });
  }, [profile, verifiedChannel, verifiedIdentifier]);

  useEffect(() => {
    if (initialCouponCode && !appliedCoupon && !couponValid) {
      setCouponCode(initialCouponCode);
    }
  }, [initialCouponCode, appliedCoupon, couponValid]);

  const orderItems = useMemo(() => items.map((item) => {
    const totalQuantity = calcTotalQuantity(item, persons);
    const lineTotal = calcLineTotal(item, persons);
    return {
      foodItemId: item.foodItemId,
      name: item.name,
      rate: item.rate,
      qtyPerPerson: Number(item.qtyPerPerson) || 0,
      servingUnit: item.servingUnit || 'pcs',
      scaleByPersons: item.scaleByPersons !== false,
      totalQuantity,
      lineTotal
    };
  }), [items, persons]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      clearCouponState();
      return;
    }

    try {
      setValidatingCoupon(true);
      setError('');
      const response = await validateTsrFoodCoupon({
        couponCode: code,
        persons,
        items: orderItems
      });
      const discount = Number(response?.data?.couponDiscountAmount || 0);
      const total = Number(response?.data?.totalAmount ?? packageTotal);
      setAppliedCoupon(response?.data?.couponCode || code);
      setCouponDiscount(discount);
      setOrderTotal(total);
      setCouponValid(true);
      setCouponMessage(discount > 0 ? `Coupon applied. You save ${formatAmount(discount)}.` : 'Coupon applied.');
    } catch (err) {
      clearCouponState();
      setCouponMessage(err.message || 'Unable to apply coupon.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleCouponCodeChange = (value) => {
    setCouponCode(value);
    if (couponValid || appliedCoupon) {
      clearCouponState();
    }
  };

  const validateForm = () => {
    if (!form.customerName.trim()) return 'Full name is required.';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length !== 10) return 'Valid 10-digit phone is required.';
    if (!form.email.trim() || !form.email.includes('@')) return 'Valid email is required.';
    if (!form.addressLine1.trim()) return 'Address line 1 is required.';
    if (!form.city.trim()) return 'City is required.';
    if (!form.state.trim()) return 'State is required.';
    if (!/^\d{6}$/.test(form.pincode.trim())) return 'Pincode must be 6 digits.';
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const response = await submitTsrFoodOrder({
        foodPackageId: packageState.foodPackageId,
        packageName: packageState.packageName,
        persons,
        notes: packageState.notes || '',
        subtotal: packageTotal,
        couponCode: appliedCoupon || '',
        customer: {
          customerName: form.customerName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          addressLine1: form.addressLine1.trim(),
          addressLine2: form.addressLine2.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim()
        },
        items: orderItems
      });

      onClose();
      navigate('/tsr-order/success', {
        state: {
          orderReference: response?.data?.orderReference,
          tsrFoodOrderId: response?.data?.tsrFoodOrderId,
          packageName: packageState.packageName,
          totalAmount: response?.data?.totalAmount ?? orderTotal,
          paymentStatus: response?.data?.paymentStatus || 'Pending',
          couponCode: response?.data?.couponCode || appliedCoupon || '',
          couponDiscountAmount: response?.data?.couponDiscountAmount ?? couponDiscount,
          emailSent: response?.data?.emailSent
        }
      });
    } catch (err) {
      setError(err.message || 'Unable to place order.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal-body">
        <div className="border rounded-3 p-3 mb-3 bg-light">
          <div className="d-flex justify-content-between mb-2">
            <span className="fw-semibold">{packageState.packageName}</span>
            <span className="text-primary fw-semibold">{formatAmount(orderTotal)}</span>
          </div>
          <small className="text-muted d-block mb-2">{persons} persons · {items.length} items</small>
          <div className="d-flex justify-content-between small">
            <span>Subtotal</span>
            <span>{formatAmount(packageTotal)}</span>
          </div>
          {couponDiscount > 0 ? (
            <div className="d-flex justify-content-between small text-success">
              <span>Coupon discount</span>
              <span>-{formatAmount(couponDiscount)}</span>
            </div>
          ) : null}
        </div>

        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <h6 className="mb-3">Delivery details</h6>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label">Full name</label>
            <input
              type="text"
              className="form-control"
              value={form.customerName}
              onChange={(event) => updateField('customerName', event.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={form.phone}
              onChange={(event) => updateField('phone', event.target.value.replace(/\D/g, '').slice(0, 10))}
              readOnly={verifiedChannel === 'phone'}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
              readOnly={verifiedChannel === 'email'}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              className="form-control"
              value={form.pincode}
              onChange={(event) => updateField('pincode', event.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Address line 1</label>
            <input
              type="text"
              className="form-control"
              value={form.addressLine1}
              onChange={(event) => updateField('addressLine1', event.target.value)}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Address line 2 (optional)</label>
            <input
              type="text"
              className="form-control"
              value={form.addressLine2}
              onChange={(event) => updateField('addressLine2', event.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-control"
              value={form.city}
              onChange={(event) => updateField('city', event.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">State</label>
            <input
              type="text"
              className="form-control"
              value={form.state}
              onChange={(event) => updateField('state', event.target.value)}
            />
          </div>
        </div>

        <h6 className="mb-2">Coupon code</h6>
        <div className="input-group mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(event) => handleCouponCodeChange(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleApplyCoupon}
            disabled={validatingCoupon}
          >
            {validatingCoupon ? 'Applying...' : 'Apply'}
          </button>
        </div>
        {couponMessage ? (
          <p className={`small mb-2 ${couponValid ? 'text-success' : 'text-danger'}`}>{couponMessage}</p>
        ) : null}
        {appliedCoupon ? <span className="badge bg-secondary mb-3">{appliedCoupon}</span> : null}

        <h6 className="mb-2 mt-3">Order items</h6>
        <div className="d-flex flex-column gap-1">
          {orderItems.map((item) => (
            <div key={item.foodItemId} className="d-flex justify-content-between small">
              <span>
                {item.name}
                {' '}
                <span className="text-muted">({formatQtyWithUnit(item.totalQuantity, item.servingUnit)})</span>
              </span>
              <span>{formatAmount(item.lineTotal)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="modal-footer justify-content-between">
        <button type="button" className="btn btn-light" onClick={onBack}>Back</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Placing order...' : 'Proceed to Checkout'}
        </button>
      </div>
    </>
  );
}

export default PlaceOrderStep;
